/**
 * Hero Slider JavaScript
 * - Auto-play with configurable timing
 * - Next/Prev navigation
 * - Pagination dots
 * - Touch/swipe support
 * - Keyboard navigation
 * - Accessible ARIA attributes
 * - Lazy loading with Intersection Observer
 */

class HeroSlider {
  constructor(element) {
    this.slider = element;
    this.track = element.querySelector('[data-slider-track]');
    this.slides = Array.from(element.querySelectorAll('.hero-slide'));
    this.prevButton = element.querySelector('[data-slider-prev]');
    this.nextButton = element.querySelector('[data-slider-next]');
    this.dotsContainer = element.querySelector('[data-slider-dots]');
    this.dots = Array.from(element.querySelectorAll('.hero-dot'));
    
    // Settings from data attributes
    this.autoplay = element.dataset.autoplay === 'true';
    this.interval = parseInt(element.dataset.interval) || 5000;
    this.transitionSpeed = parseInt(element.dataset.transition) || 600;
    
    // State
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.isTransitioning = false;
    
    // Touch/swipe
    this.touchStartX = 0;
    this.touchEndX = 0;
    
    this.init();
  }
  
  init() {
    if (this.slides.length === 0) return;
    
    // Apply overlay styles from data attributes (NO inline CSS)
    this.applyOverlayStyles();
    
    // Initialize responsive videos (render only appropriate video based on screen size)
    this.initResponsiveVideos();
    
    // Initialize lazy loading observer
    this.initLazyLoading();
    
    // Set initial slide
    this.goToSlide(0, false);
    
    // Event listeners
    this.attachEventListeners();
    
    // Start autoplay
    if (this.autoplay && this.slides.length > 1) {
      this.startAutoplay();
    }
    
    // Pause autoplay on hover
    this.slider.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.slider.addEventListener('mouseleave', () => this.resumeAutoplay());
    
    // Pause autoplay when tab is not visible
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAutoplay();
      } else {
        this.resumeAutoplay();
      }
    });
    
    // Handle window resize for responsive video switching
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.handleResponsiveVideoResize();
      }, 250);
    });
  }
  
  attachEventListeners() {
    // Navigation arrows
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.prev());
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.next());
    }
    
    // Bind pagination dots
    this.bindDotHandlers();
    
    // Keyboard navigation
    this.slider.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prev();
      } else if (e.key === 'ArrowRight') {
        this.next();
      }
    });
    
    // Touch/swipe support
    this.track.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    
    this.track.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }, { passive: true });
  }
  
  handleSwipe() {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;
    
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        this.next();
      } else {
        this.prev();
      }
    }
  }
  
  goToSlide(index, animate = true) {
    if (this.isTransitioning) return;
    if (index < 0 || index >= this.slides.length) return;
    
    this.isTransitioning = true;
    this.currentIndex = index;
    
    // Load current slide and adjacent slides for smooth navigation
    this.loadSlide(index);
    if (index > 0) this.loadSlide(index - 1);
    if (index < this.slides.length - 1) this.loadSlide(index + 1);
    
    // Update slides
    this.slides.forEach((slide, i) => {
      slide.classList.remove('active', 'prev', 'next');
      slide.style.transition = animate ? `opacity ${this.transitionSpeed}ms ease, transform ${this.transitionSpeed}ms ease` : 'none';
      
      if (i === index) {
        slide.classList.add('active');
        // Auto-play video when slide becomes active
        this.playSlideVideo(slide);
      } else {
        // Pause video when slide becomes inactive
        this.pauseSlideVideo(slide);
        if (i < index) {
          slide.classList.add('prev');
        } else {
          slide.classList.add('next');
        }
      }
    });
    
    // Update dots
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('active', i === index);
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
    
    // Update ARIA
    this.slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i !== index);
    });
    
    setTimeout(() => {
      this.isTransitioning = false;
    }, this.transitionSpeed);
  }
  
  next() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex);
    this.resetAutoplay();
  }
  
  prev() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(prevIndex);
    this.resetAutoplay();
  }
  
  startAutoplay() {
    if (!this.autoplay || this.slides.length <= 1) return;
    
    this.autoplayTimer = setInterval(() => {
      this.next();
    }, this.interval);
  }
  
  pauseAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
  
  resumeAutoplay() {
    if (this.autoplay && !this.autoplayTimer && this.slides.length > 1) {
      this.startAutoplay();
    }
  }
  
  resetAutoplay() {
    if (this.autoplay) {
      this.pauseAutoplay();
      this.resumeAutoplay();
    }
  }
  
  applyOverlayStyles() {
    // Apply overlay color/opacity from data attributes using CSS custom properties
    this.slides.forEach(slide => {
      const background = slide.querySelector('.slide-background');
      const overlay = slide.querySelector('.slide-overlay');
      
      if (background && overlay) {
        const color = background.dataset.overlayColor;
        const opacity = background.dataset.overlayOpacity;
        
        if (color && opacity) {
          // Set CSS custom properties instead of inline styles
          overlay.style.setProperty('--overlay-color', color);
          overlay.style.setProperty('--overlay-opacity', opacity / 100);
        }
      }
    });
  }
  
  initResponsiveVideos() {
    // Render appropriate video/image based on current screen size
    if (!this.slider) return;
    
    const videoContainers = this.slider.querySelectorAll('[data-video-container]');
    if (!videoContainers || videoContainers.length === 0) return;
    
    videoContainers.forEach(container => {
      this.renderResponsiveVideo(container);
    });
  }
  
  renderResponsiveVideo(container) {
    if (!container) return;
    
    const isMobile = window.innerWidth <= 768;
    const isFirstSlide = container.dataset.isFirstSlide === 'true';
    const slideIndex = container.dataset.slideIndex || '0';
    
    const desktopVideo = container.dataset.desktopVideo || '';
    const desktopPoster = container.dataset.desktopPoster || '';
    const mobileVideo = container.dataset.mobileVideo || '';
    const mobilePoster = container.dataset.mobilePoster || '';
    const mobileFallback = container.dataset.mobileFallback || '';
    const mobileFallbackSrcset = container.dataset.mobileFallbackSrcset || '';
    
    // Clear existing content
    container.innerHTML = '';
    
    if (isMobile) {
      // Mobile: Use mobile video if available, otherwise fallback image
      if (mobileVideo && mobileVideo !== 'null' && mobileVideo !== '') {
        // Render mobile video
        const video = document.createElement('video');
        video.className = 'hero-video';
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        if (mobilePoster && mobilePoster !== 'null') {
          video.poster = mobilePoster;
        }
        video.setAttribute('data-testid', `hero-mobile-video-${slideIndex}`);
        
        if (!isFirstSlide) {
          video.setAttribute('data-lazy-video', '');
        }
        
        const source = document.createElement('source');
        if (isFirstSlide) {
          source.src = mobileVideo;
        } else {
          source.setAttribute('data-src', mobileVideo);
        }
        source.type = 'video/mp4';
        
        video.appendChild(source);
        container.appendChild(video);
        
        // Auto-play first slide video
        if (isFirstSlide) {
          video.load();
          video.play().catch(() => {});
        }
      } else if (mobileFallback && mobileFallback !== 'null' && mobileFallback !== '') {
        // Render mobile fallback image
        const picture = document.createElement('picture');
        picture.className = 'hero-mobile-fallback';
        
        const img = document.createElement('img');
        if (isFirstSlide) {
          img.src = mobileFallback;
          if (mobileFallbackSrcset && mobileFallbackSrcset !== 'null') {
            img.srcset = mobileFallbackSrcset;
          }
          img.loading = 'eager';
        } else {
          img.setAttribute('data-src', mobileFallback);
          if (mobileFallbackSrcset && mobileFallbackSrcset !== 'null') {
            img.setAttribute('data-srcset', mobileFallbackSrcset);
          }
          img.loading = 'lazy';
        }
        img.alt = '';
        img.setAttribute('data-testid', `hero-mobile-fallback-${slideIndex}`);
        
        picture.appendChild(img);
        container.appendChild(picture);
      } else {
        // No mobile content, use desktop video on mobile
        this.renderDesktopVideo(container, desktopVideo, desktopPoster, isFirstSlide, slideIndex);
      }
    } else {
      // Desktop: Always use desktop video
      this.renderDesktopVideo(container, desktopVideo, desktopPoster, isFirstSlide, slideIndex);
    }
  }
  
  renderDesktopVideo(container, videoUrl, posterUrl, isFirstSlide, slideIndex) {
    if (!videoUrl || videoUrl === 'null' || videoUrl === '') return;
    
    const video = document.createElement('video');
    video.className = 'hero-video';
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    if (posterUrl && posterUrl !== 'null') {
      video.poster = posterUrl;
    }
    video.setAttribute('data-testid', `hero-video-${slideIndex}`);
    
    if (!isFirstSlide) {
      video.setAttribute('data-lazy-video', '');
    }
    
    const source = document.createElement('source');
    if (isFirstSlide) {
      source.src = videoUrl;
    } else {
      source.setAttribute('data-src', videoUrl);
    }
    source.type = 'video/mp4';
    
    video.appendChild(source);
    container.appendChild(video);
    
    // Auto-play first slide video
    if (isFirstSlide) {
      video.load();
      video.play().catch(() => {});
    }
  }
  
  handleResponsiveVideoResize() {
    // Re-render videos if screen size crosses mobile threshold
    const wasMobile = this.wasMobile ?? (window.innerWidth <= 768);
    const isMobile = window.innerWidth <= 768;
    
    // Only re-render if crossing the threshold
    if (wasMobile !== isMobile) {
      this.initResponsiveVideos();
      
      // Reload current slide to ensure video plays
      if (this.slides && this.slides[this.currentIndex]) {
        const currentSlide = this.slides[this.currentIndex];
        this.playSlideVideo(currentSlide);
      }
    }
    
    this.wasMobile = isMobile;
  }
  
  initLazyLoading() {
    // Intersection Observer for lazy loading
    const options = {
      root: null,
      rootMargin: '50px', // Load slightly before entering viewport
      threshold: 0.01
    };
    
    this.lazyObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const slideIndex = parseInt(entry.target.dataset.slideIndex);
          this.loadSlide(slideIndex);
        }
      });
    }, options);
    
    // Eagerly load first slide
    this.loadSlide(0);
    
    // Observe all other slides for lazy loading
    this.slides.forEach((slide, index) => {
      if (index !== 0) {
        this.lazyObserver.observe(slide);
      }
    });
    
    // Watch for DOM changes (Theme Editor block reordering)
    if (this.track) {
      this.mutationObserver = new MutationObserver(() => {
        this.handleSlideChanges();
      });
      
      this.mutationObserver.observe(this.track, {
        childList: true,
        subtree: false
      });
    }
  }
  
  handleSlideChanges() {
    // Recompute slides, dots, and dots container
    this.slides = Array.from(this.slider.querySelectorAll('.hero-slide'));
    this.dotsContainer = this.slider.querySelector('[data-slider-dots]');
    this.dots = Array.from(this.slider.querySelectorAll('.hero-dot'));
    
    // Find which slide is actually active (by class) or default to first
    let newIndex = 0;
    const activeSlide = this.slides.find(slide => slide.classList.contains('active'));
    if (activeSlide) {
      newIndex = this.slides.indexOf(activeSlide);
    }
    
    // Clamp index to valid range
    if (newIndex < 0 || newIndex >= this.slides.length) {
      newIndex = 0;
    }
    
    this.currentIndex = newIndex;
    
    // Disconnect and recreate lazy observer
    if (this.lazyObserver) {
      this.lazyObserver.disconnect();
    }
    
    // Reinitialize lazy loading
    const options = {
      root: null,
      rootMargin: '50px',
      threshold: 0.01
    };
    
    this.lazyObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const slideIndex = parseInt(entry.target.dataset.slideIndex);
          this.loadSlide(slideIndex);
        }
      });
    }, options);
    
    // Always hydrate the first slide after reordering
    this.loadSlide(0);
    
    // Re-observe all slides except current
    this.slides.forEach((slide, index) => {
      if (index !== this.currentIndex && index !== 0) {
        this.lazyObserver.observe(slide);
      }
    });
    
    // Rebind pagination dot handlers for new DOM
    this.bindDotHandlers();
    
    // Hydrate current slide and update UI
    this.goToSlide(this.currentIndex, false);
  }
  
  bindDotHandlers() {
    if (!this.dotsContainer) return;
    
    // Remove old delegated handler first
    if (this.dotClickHandler) {
      this.dotsContainer.removeEventListener('click', this.dotClickHandler);
    }
    
    // Create new delegated handler
    this.dotClickHandler = (e) => {
      const dot = e.target.closest('.hero-dot');
      if (dot) {
        const index = Array.from(this.dots).indexOf(dot);
        if (index !== -1) {
          this.goToSlide(index);
        }
      }
    };
    
    // Attach single delegated listener to container
    this.dotsContainer.addEventListener('click', this.dotClickHandler);
  }
  
  loadSlide(index) {
    const slide = this.slides[index];
    if (!slide) return;
    
    // Check if slide has already been loaded (per-element flag)
    if (slide.dataset.loaded === 'true') {
      return;
    }
    
    // Load source tags with data-srcset (for mobile images in picture element)
    const lazySources = slide.querySelectorAll('source[data-srcset]');
    lazySources.forEach(source => {
      if (source.dataset.srcset) {
        source.srcset = source.dataset.srcset;
        delete source.dataset.srcset;
      }
    });
    
    // Load images with data-src (always check for presence)
    const lazyImages = slide.querySelectorAll('img[data-src]');
    lazyImages.forEach(img => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        delete img.dataset.src;
      }
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
        delete img.dataset.srcset;
      }
    });
    
    // Load videos with data-lazy-video (always check for presence)
    const lazyVideos = slide.querySelectorAll('video[data-lazy-video]');
    lazyVideos.forEach(video => {
      const sources = video.querySelectorAll('source[data-src]');
      sources.forEach(source => {
        if (source.dataset.src) {
          source.src = source.dataset.src;
          delete source.dataset.src;
        }
      });
      video.load();
      delete video.dataset.lazyVideo;
    });
    
    // Mark slide as loaded
    slide.dataset.loaded = 'true';
  }
  
  playSlideVideo(slide) {
    const video = slide.querySelector('video');
    if (video && !video.dataset.lazyVideo) {
      video.play().catch(() => {
        // Auto-play failed, probably needs user interaction
      });
    }
  }
  
  pauseSlideVideo(slide) {
    const video = slide.querySelector('video');
    if (video) {
      video.pause();
    }
  }
  
  destroy() {
    this.pauseAutoplay();
    if (this.lazyObserver) {
      this.lazyObserver.disconnect();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    // Remove event listeners if needed
  }
}

// Initialize all sliders on the page
function initHeroSliders() {
  const sliders = document.querySelectorAll('[data-hero-slider]');
  sliders.forEach(slider => {
    new HeroSlider(slider);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeroSliders);
} else {
  initHeroSliders();
}

// Re-initialize on Shopify theme editor section load
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.detail.sectionId) {
      const section = document.querySelector(`[data-section-id="${event.detail.sectionId}"]`);
      if (section && section.querySelector('[data-hero-slider]')) {
        const slider = section.querySelector('[data-hero-slider]');
        new HeroSlider(slider);
      }
    }
  });
}
